import React from "react";
import Modal from "Utils/Modal";
import { isValidToken } from "Api";
import RegionDropdown from "Utils/RegionDropdown";
import PostalCodeInput from "Utils/PostalCodeInput";
import StripeCardIframe from "./StripeCardIframe";

// Rails may prepend this to JSON responses; JSON.parse fails without stripping.
const JSON_PROTECTION_PREFIX = /^\)\]\}',?\n/;

const parseJsonBody = (raw) => {
  if (raw == null || raw === "") return null;
  if (typeof raw !== "string") {
    return typeof raw === "object" && !Array.isArray(raw) ? raw : null;
  }
  const t = raw.trim().replace(JSON_PROTECTION_PREFIX, "");
  if (!t || t.startsWith("<") || t.startsWith("<!")) return null;
  try {
    return JSON.parse(t);
  } catch (e) {
    return null;
  }
};

/**
 * Modal with Stripe card input plus required billing fields. Saves the card
 * via portal API and calls onCardSaved(creditCard) so the parent can refresh and close.
 */
class StripeSecureCardModal extends React.Component {
  static defaultProps = {
    /** Guest checkout (not signed in): must be true so API sets added_via storefront_guest. */
    guestPayment: false,
  };

  stripeCardRef = React.createRef();

  state = {
    name: "",
    country: "USA",
    streetAddress1: "",
    city: "",
    locale: "",
    postalCode: "",
    /** Human-readable lines from API `full_messages` or flattened `errors` */
    errorLines: null,
    savingCard: false,
    success: false,
  };

  componentDidUpdate(prevProps) {
    if (this.props.open && !prevProps.open) {
      this.setState({
        name: "",
        country: "USA",
        streetAddress1: "",
        city: "",
        locale: "",
        postalCode: "",
        errorLines: null,
        savingCard: false,
        success: false,
      });
    }
    if (!this.props.open && prevProps.open) {
      this.setState({
        errorLines: null,
        savingCard: false,
        success: false,
      });
    }
  }

  onFieldChange = (e) => {
    const { name, value } = e.target;
    this.setState({ [name]: value, errorLines: null });
  };

  /** Parsed JSON from fetch failure or axios-style error */
  getFailurePayload = (err) => {
    let data =
      err?.parsedBody != null && typeof err.parsedBody === "object"
        ? err.parsedBody
        : parseJsonBody(err?.parsedBody);

    if (
      (!data || (typeof data === "object" && Object.keys(data).length === 0)) &&
      err?.rawResponseText
    ) {
      data = parseJsonBody(err.rawResponseText) || data;
    }

    if (data == null && err?.response?.data != null) {
      const rd = err.response.data;
      data = typeof rd === "string" ? parseJsonBody(rd) || { error: rd } : rd;
    }

    return data && typeof data === "object" && !Array.isArray(data) ? data : null;
  };

  /**
   * Matches portal credit_cards JSON: use `full_messages` first, then flatten `errors`.
   */
  errorLinesFromPayload = (data, err) => {
    const lines = [];

    if (data && typeof data === "object") {
      if (Array.isArray(data.full_messages) && data.full_messages.length > 0) {
        data.full_messages.forEach((m) => {
          if (m != null && String(m).trim()) lines.push(String(m).trim());
        });
        if (lines.length > 0) return lines;
      }

      let nested = data.errors;
      if (typeof nested === "string") {
        nested = parseJsonBody(nested);
      }
      if (nested && typeof nested === "object" && !Array.isArray(nested)) {
        Object.values(nested).forEach((msgs) => {
          const list = Array.isArray(msgs) ? msgs : [msgs];
          list.forEach((m) => {
            if (m != null && String(m).trim()) lines.push(String(m).trim());
          });
        });
        if (lines.length > 0) return lines;
      }

      const reserved = new Set(["error", "message", "full_messages", "errors"]);
      const entries = Object.entries(data).filter(([k]) => !reserved.has(k));
      const looksFlat =
        entries.length > 0 &&
        entries.every(
          ([, v]) =>
            Array.isArray(v) &&
            v.every((m) => m == null || typeof m === "string")
        );
      if (looksFlat) {
        entries.forEach(([, v]) => {
          v.forEach((m) => {
            if (m != null && String(m).trim()) lines.push(String(m).trim());
          });
        });
        if (lines.length > 0) return lines;
      }

      const one =
        (typeof data.message === "string" && data.message.trim()) ||
        (typeof data.error === "string" && data.error.trim()) ||
        "";
      if (one) return [one];
    }

    const fallback = err?.message || "Failed to save card";
    return [fallback];
  };

  handleSubmit = async () => {
    const { location, onCardSaved, onError, customerId, customerType } = this.props;
    const {
      name,
      country,
      streetAddress1,
      city,
      locale,
      postalCode,
    } = this.state;

    if (!this.stripeCardRef.current) {
      this.setState({ errorLines: ["Card entry is not ready."] });
      return;
    }

    try {
      const result = await this.stripeCardRef.current.tokenize();
      this.setState({ savingCard: true, errorLines: null });

      const payload = {
        nickname: this.props.nickname || "New Card",
        name: name || undefined,
        country: country || "USA",
        street_address1: streetAddress1,
        city,
        locale,
        postal_code: postalCode,
        stripe_token: result.token,
        guest_payment: this.props.guestPayment === true,
      };

      const isGuestCheckout =
        customerId !== undefined && customerId !== null && String(customerId).trim() !== "";
      if (isGuestCheckout) {
        payload.customer_id = customerId;
        payload.owner_type = customerType || "Customer";
      }

      const url = `${process.env.REACT_APP_API_DOMAIN}/api/portal/credit_cards`;
      const requestBody = JSON.stringify({
        location_id: location.id,
        credit_card: payload,
      });
      const headers = {
        Accept: "application/json",
        "Content-Type": "application/json",
      };
      // Send session headers whenever the user is signed in (even if customer_id is also sent for rental checkout).
      if (isValidToken()) {
        try {
          const auth = JSON.parse(localStorage.getItem("authTokenHeaders"));
          if (auth && typeof auth === "object") {
            Object.keys(auth).forEach((key) => {
              if (key === "Content-Type") return;
              if (auth[key] != null && auth[key] !== "") {
                headers[key] = auth[key];
              }
            });
          }
        } catch (e) {
          /* ignore */
        }
      }

      const res = await fetch(url, {
        method: "POST",
        headers,
        body: requestBody,
      });

      const rawText = await res.text();
      const parsedBody = parseJsonBody(rawText) || (rawText && rawText.trim() ? { error: rawText.trim() } : {});

      if (!res.ok) {
        const failErr = new Error(`Request failed with status code ${res.status}`);
        failErr.status = res.status;
        failErr.parsedBody = parsedBody;
        failErr.rawResponseText = rawText;
        throw failErr;
      }

      this.setState({ success: true, savingCard: false });

      const creditCard = parsedBody;
      setTimeout(() => {
        if (onCardSaved && creditCard) {
          onCardSaved(creditCard);
        }
        // Do not call toggle() here: every parent onCardSaved already closes the modal.
        // Calling toggle again would reopen it (double-toggle), matching WorldpayHostedPaymentModal behavior.
      }, 2500);
    } catch (err) {
      const data = this.getFailurePayload(err);
      const errorLines = this.errorLinesFromPayload(data, err);
      this.setState({ errorLines, savingCard: false });
      if (onError) {
        onError(errorLines.join("\n"));
      }
    }
  };

  render() {
    const { open, toggle, title, location } = this.props;
    const {
      name,
      country,
      streetAddress1,
      city,
      locale,
      postalCode,
      errorLines,
      savingCard,
      success,
    } = this.state;

    const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";
    const billingDetails = {
      name: name || "",
      nickname: "",
      streetAddress1: streetAddress1 || "",
      streetAddress2: "",
      city: city || "",
      locale: locale || "",
      postalCode: postalCode || "",
      country: country || "USA",
    };

    return (
      <Modal
        open={open}
        toggle={toggle}
        title={title || "Add Credit Card"}
        large={true}
        className="stripe-secure-card-modal"
        actions={[
          <button key="cancel" className="btn cancel" onClick={toggle}>
            Cancel
          </button>,
        ]}
      >
        <div
          className="stripe-secure-card-modal__body"
          style={{ minHeight: "360px", position: "relative" }}
        >
          {success && (
            <div style={{ padding: "32px", textAlign: "center" }}>
              <div
                style={{
                  fontSize: "48px",
                  color: "#4caf50",
                  marginBottom: "16px",
                }}
              >
                ✓
              </div>
              <h3 style={{ color: "#4caf50", marginBottom: "8px" }}>
                Card Saved Successfully!
              </h3>
              <p style={{ color: "#666" }}>
                Your credit card has been securely stored.
              </p>
            </div>
          )}

          {savingCard && !success && (
            <div style={{ padding: "32px", textAlign: "center" }}>
              <p style={{ fontSize: "18px", marginBottom: "8px" }}>
                Saving your card...
              </p>
              <p style={{ fontSize: "14px", color: "#666" }}>
                Please wait while we securely store your payment information.
              </p>
            </div>
          )}

          {errorLines && errorLines.length > 0 && (
            <div
              style={{
                padding: "16px",
                backgroundColor: "#fee",
                color: "#c33",
                borderRadius: "4px",
                margin: "16px 0",
              }}
              role="alert"
            >
              <strong>Error:</strong>
              <ul
                style={{
                  margin: "8px 0 0",
                  paddingLeft: "20px",
                  listStyleType: "disc",
                }}
              >
                {errorLines.map((line, i) => (
                  <li key={i} style={{ marginBottom: "4px" }}>
                    {line}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!success && !savingCard && (
            <div className="form stripe-secure-card-modal__form">
              <div className="fields">
                <h4>Billing address</h4>
                <select
                  name="country"
                  value={country}
                  onChange={this.onFieldChange}
                >
                  <option value="">Select country</option>
                  <option value="USA">United States</option>
                  <option value="CAN">Canada</option>
                </select>
                <input
                  name="streetAddress1"
                  type="text"
                  placeholder="Street address"
                  className="stripe-modal-full"
                  value={streetAddress1}
                  onChange={this.onFieldChange}
                />
                <input
                  name="city"
                  type="text"
                  placeholder="City"
                  className="stripe-modal-third"
                  value={city}
                  onChange={this.onFieldChange}
                />
                <RegionDropdown
                  name="locale"
                  defaultOptionLabel={country === "USA" ? "State" : "Province"}
                  classes="stripe-modal-third"
                  disableWhenEmpty={true}
                  countryValueType="short"
                  labelType="short"
                  valueType="short"
                  country={country}
                  value={locale}
                  onChange={this.onFieldChange}
                />
                <PostalCodeInput
                  name="postalCode"
                  type="text"
                  placeholder="ZIP / Postal code"
                  className="stripe-modal-third"
                  country={country}
                  maxLength={country === "USA" ? 10 : 7}
                  value={postalCode}
                  onChange={this.onFieldChange}
                />
              </div>
              <div className="fields">
                <h4>Payment method</h4>
                <input
                  name="name"
                  type="text"
                  placeholder="Name on Card"
                  className="stripe-modal-full"
                  value={name}
                  onChange={this.onFieldChange}
                />
                <StripeCardIframe
                  ref={this.stripeCardRef}
                  publishableKey={publishableKey}
                  billingDetails={billingDetails}
                />
              </div>
              <div className="stripe-secure-card-modal__save-wrap">
                <button
                  type="button"
                  className="btn stripe-secure-card-modal__save"
                  onClick={this.handleSubmit}
                >
                  Save Card
                </button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    );
  }
}

export default StripeSecureCardModal;
